import type { LucideIcon } from 'lucide-react';
import {
  Award,
  BadgeCheck,
  Briefcase,
  Building2,
  Coffee,
  Compass,
  CreditCard,
  Crown,
  Factory,
  Flame,
  Gift,
  GraduationCap,
  Globe,
  Handshake,
  Heart,
  Home,
  Landmark,
  MapPin,
  Megaphone,
  Monitor,
  Network,
  Package,
  Percent,
  Phone,
  Rocket,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Star,
  Store,
  Tags,
  Target,
  Ticket,
  TrendingUp,
  Truck,
  UserPlus,
  Users,
  Wallet,
  Zap,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { SalesChannel } from '@/lib/api';

// `value` is persisted in `sales_channels.icon`; keep existing values stable.
export const CHANNEL_ICON_OPTIONS: Array<{ value: string; label: string; icon: LucideIcon }> = [
  { value: 'tags', label: '标签', icon: Tags },
  { value: 'store', label: '自营', icon: Store },
  { value: 'building', label: '楼宇', icon: Building2 },
  { value: 'handshake', label: '合作', icon: Handshake },
  { value: 'users', label: '团队', icon: Users },
  { value: 'megaphone', label: '推广', icon: Megaphone },
  { value: 'crown', label: '会员', icon: Crown },
  { value: 'target', label: '目标', icon: Target },
  { value: 'shopping-cart', label: '商城', icon: ShoppingCart },
  { value: 'shopping-bag', label: '零售', icon: ShoppingBag },
  { value: 'globe', label: '线上', icon: Globe },
  { value: 'map-pin', label: '门店', icon: MapPin },
  { value: 'phone', label: '电话', icon: Phone },
  { value: 'briefcase', label: '商务', icon: Briefcase },
  { value: 'gift', label: '活动', icon: Gift },
  { value: 'star', label: '明星', icon: Star },
  { value: 'heart', label: '喜爱', icon: Heart },
  { value: 'zap', label: '闪购', icon: Zap },
  { value: 'rocket', label: '增长', icon: Rocket },
  { value: 'trending-up', label: '趋势', icon: TrendingUp },
  { value: 'award', label: '奖励', icon: Award },
  { value: 'badge-check', label: '认证', icon: BadgeCheck },
  { value: 'truck', label: '物流', icon: Truck },
  { value: 'home', label: '家居', icon: Home },
  { value: 'factory', label: '厂商', icon: Factory },
  { value: 'landmark', label: '机构', icon: Landmark },
  { value: 'network', label: '网络', icon: Network },
  { value: 'user-plus', label: '拉新', icon: UserPlus },
  { value: 'smartphone', label: '移动', icon: Smartphone },
  { value: 'monitor', label: '电脑', icon: Monitor },
  { value: 'coffee', label: '生活', icon: Coffee },
  { value: 'sparkles', label: '推荐', icon: Sparkles },
  { value: 'graduation-cap', label: '教育', icon: GraduationCap },
  { value: 'percent', label: '优惠', icon: Percent },
  { value: 'ticket', label: '券', icon: Ticket },
  { value: 'flame', label: '热门', icon: Flame },
  { value: 'compass', label: '探索', icon: Compass },
  { value: 'package', label: '包裹', icon: Package },
  { value: 'wallet', label: '钱包', icon: Wallet },
  { value: 'credit-card', label: '支付', icon: CreditCard },
];

export function resolveChannelIcon(icon?: string | null): LucideIcon {
  return CHANNEL_ICON_OPTIONS.find((item) => item.value === icon)?.icon ?? Tags;
}

/** Icon picker shared by 新增/编辑 渠道 — each option renders its glyph + label. */
export function ChannelIconSelect({
  value,
  onChange,
  triggerClassName,
}: {
  value: string;
  onChange: (value: string) => void;
  triggerClassName?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={triggerClassName}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {CHANNEL_ICON_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <SelectItem key={option.value} value={option.value}>
              <span className="flex items-center gap-2">
                <Icon className="h-3.5 w-3.5 shrink-0" />
                {option.label}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

export function ChannelPill({ channel }: { channel?: SalesChannel | null }) {
  if (!channel) {
    return <Badge variant="muted">未分渠道</Badge>;
  }
  const Icon = resolveChannelIcon(channel.icon);
  // Built on the shared Badge; the channel's brand color is applied inline so
  // each channel keeps its own hue while still using the component primitive.
  return (
    <Badge
      variant="outline"
      className="gap-1"
      style={{ borderColor: channel.color, color: channel.color, backgroundColor: `${channel.color}12` }}
    >
      <Icon className="h-3 w-3" />
      {channel.badge_label}
    </Badge>
  );
}
